import { JsonSchema } from '@core/models/transformers.model';

import { LocaleTranslationObject } from '@shared/types/translation.type';

import {
  COMPAT_TARGETS,
  CompatTarget,
  CompatibilityCondition,
  FILTER_OP_SHAPES,
  FieldPath,
  LiteralKind,
  Quantifier,
  RoutePosition,
  isFilterOpName,
  pathKey
} from '../wire';
import {
  FieldType,
  FilterAssociation,
  FilterField,
  FilterVocabulary,
  StaticVocabulary
} from './vocabulary';

interface CompatEntry {
  path: { name: string; collection: boolean }[];
  operators: string[];
  'x-filterable'?: { type: string; uidEntityType?: string };
  'x-i18n-title'?: LocaleTranslationObject;
}

interface CompatJunction {
  edge: string;
  quantifiers?: Quantifier[];
  fields: CompatEntry[];
  'x-i18n-title'?: LocaleTranslationObject;
}

export interface CompatibilityConditionKind {
  kind: CompatibilityCondition['kind'];
  enforceable: boolean;
  positions?: RoutePosition[];
}

const TYPE_BY_SERVED: Record<string, FieldType> = {
  UID: 'UID',
  UID_LIST: 'UID_LIST',
  NUMBER: 'NUMBER',
  INTEGER: 'INTEGER',
  BOOL: 'BOOL',
  TEXT: 'TEXT',
  MASS: 'MASS',
  DURATION: 'DURATION',
  LENGTH: 'LENGTH',
  DATE: 'DATE',
  MULTI_SELECT: 'TAGS',
  LABELS: 'LABELS'
};

/** Compatibility entries carry no literal metadata, so the kinds follow from the field type. */
const LITERAL_KINDS_BY_TYPE: Record<FieldType, readonly LiteralKind[]> = {
  UID: ['uid'],
  UID_LIST: ['uid'],
  NUMBER: ['number', 'integer'],
  INTEGER: ['integer'],
  BOOL: ['bool'],
  TEXT: ['string'],
  MASS: ['mass'],
  DURATION: ['duration'],
  LENGTH: ['length'],
  DATE: ['date', 'date_time'],
  DATE_TIME: ['date_time'],
  TAGS: [],
  LABELS: ['string', 'number', 'integer', 'bool', 'date', 'uid'],
  LIST: ['string']
};

/** Label operators compare references to a label, so they take a uid whatever the key type. */
const LABEL_REFERENCE_OPS: ReadonlySet<string> = new Set([
  'label_references',
  'label_references_any_of',
  'label_references_none_of'
]);

const LITERAL_SHAPES = new Set([
  'literal',
  'range',
  'literals',
  'label_literal',
  'label_literals',
  'label_range'
]);

function entryToField(entry: CompatEntry): FilterField {
  const path: FieldPath = {
    segments: entry.path.map((segment) =>
      segment.collection
        ? { collection: { name: segment.name, quantifier: 'any' as Quantifier } }
        : { field: { name: segment.name } }
    )
  };
  const type = TYPE_BY_SERVED[entry['x-filterable']?.type ?? 'TEXT'] ?? 'TEXT';
  const operators = entry.operators.filter(isFilterOpName);
  const literalKinds: FilterField['literalKinds'] = {};
  for (const op of operators) {
    if (LITERAL_SHAPES.has(FILTER_OP_SHAPES[op]))
      literalKinds[op] = LABEL_REFERENCE_OPS.has(op)
        ? ['uid']
        : LITERAL_KINDS_BY_TYPE[type];
  }
  return {
    key: pathKey(path),
    path,
    label: entry['x-i18n-title'] ?? null,
    type,
    operators,
    literalKinds,
    ...(entry['x-filterable']?.uidEntityType
      ? { uidEntityType: entry['x-filterable'].uidEntityType }
      : {}),
    ...(entry.path[0]?.collection ? { collection: true } : {})
  };
}

function servedKey(target: CompatTarget): string {
  return target.toUpperCase();
}

export function compatibilityTargets(carrier: JsonSchema): CompatTarget[] {
  const served = Object.keys(
    (carrier['x-compatibility-fields'] as Record<string, unknown> | undefined) ?? {}
  );
  return COMPAT_TARGETS.filter((target) => served.includes(servedKey(target)));
}

export function compatibilityVocabulary(
  carrier: JsonSchema,
  target: CompatTarget
): FilterVocabulary {
  const entries =
    (carrier['x-compatibility-fields'] as Record<string, CompatEntry[]> | undefined)?.[
      servedKey(target)
    ] ?? [];
  const junctions =
    (
      carrier['x-compatibility-junctions'] as Record<string, CompatJunction[]> | undefined
    )?.[servedKey(target)] ?? [];
  const selfEntries =
    (carrier['x-compatibility-self-fields'] as CompatEntry[] | undefined) ?? [];
  const associations: FilterAssociation[] = junctions.map((junction) => ({
    edge: junction.edge,
    label: junction['x-i18n-title'] ?? null,
    quantifiers: junction.quantifiers ?? ['any', 'all'],
    cardinality: 'many',
    targetEntityType: null,
    sortableLeaves: []
  }));
  return new StaticVocabulary({
    entityType: servedKey(target),
    allowsSelfRef: true,
    allowsRelativeDate: false,
    root: entries.map(entryToField),
    scoped: new Map(
      junctions.map((junction) => [junction.edge, junction.fields.map(entryToField)])
    ),
    associations,
    selfFields: selfEntries.map(entryToField),
    sortable: []
  });
}

interface ServedConditions {
  kinds?: CompatibilityConditionKind[];
  contextFields?: CompatEntry[];
}

export function compatibilityConditionKinds(
  carrier: JsonSchema
): CompatibilityConditionKind[] {
  return (
    (carrier['x-compatibility-conditions'] as ServedConditions | undefined)?.kinds ?? []
  ).map((kind) => ({
    kind: kind.kind,
    enforceable: kind.enforceable,
    ...(kind.positions ? { positions: kind.positions } : {})
  }));
}

export function compatibilityContextVocabulary(carrier: JsonSchema): FilterVocabulary {
  const entries =
    (carrier['x-compatibility-conditions'] as ServedConditions | undefined)
      ?.contextFields ?? [];
  return new StaticVocabulary({
    entityType: 'CONTEXT',
    allowsSelfRef: false,
    allowsRelativeDate: false,
    root: entries.map(entryToField),
    scoped: new Map(),
    associations: [],
    selfFields: [],
    sortable: []
  });
}
